import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, CheckCircle2, Undo2, Loader2, Pencil, Download } from 'lucide-react';

function statusBadgeClass(s) {
  if (s === 'finalized' || s === 'approved') return 'bg-green-100 text-green-800';
  if (s === 'draft') return 'bg-slate-200 text-slate-700';
  if (s === 'rejected') return 'bg-red-100 text-red-800';
  if (s === 'pending_approval') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-700';
}

function statusLabel(s) {
  if (s === 'pending_approval') return 'PENDING';
  return (s || '').toUpperCase();
}

export function PurchaseReturnHistory({ returns, loading, filterStatus, setFilterStatus, onView, onEdit, onFinalize, onDownloadPdf }) {
  const filteredReturns = filterStatus === 'all' ? returns : returns.filter(r => r.status === filterStatus);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="finalized">Finalized</SelectItem>
            <SelectItem value="pending_approval">Pending (legacy)</SelectItem>
            <SelectItem value="approved">Approved (legacy)</SelectItem>
            <SelectItem value="rejected">Rejected (legacy)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" /></div>
      ) : filteredReturns.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Undo2 className="w-12 h-12 mx-auto mb-3" />
          <p className="font-medium">No purchase returns found</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">PRN #</th>
                <th className="px-4 py-2 text-left font-semibold">GRN #</th>
                <th className="px-4 py-2 text-left font-semibold">Supplier</th>
                <th className="px-4 py-2 text-center font-semibold">Type</th>
                <th className="px-4 py-2 text-center font-semibold">Items</th>
                <th className="px-4 py-2 text-right font-semibold">Value</th>
                <th className="px-4 py-2 text-center font-semibold">Status</th>
                <th className="px-4 py-2 text-center font-semibold">Date</th>
                <th className="px-4 py-2 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredReturns.map(pr => {
                const isDraft = pr.status === 'draft';
                return (
                  <tr key={pr.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono font-semibold text-red-600">{pr.prn_number}</td>
                    <td className="px-4 py-2 text-blue-600">{pr.grn_number}</td>
                    <td className="px-4 py-2">{pr.supplier_name}</td>
                    <td className="px-4 py-2 text-center"><Badge variant="outline">{pr.return_type}</Badge></td>
                    <td className="px-4 py-2 text-center">{pr.items?.length || 0}</td>
                    <td className="px-4 py-2 text-right font-mono">₹{(pr.total_return_value || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-2 text-center">
                      <Badge className={statusBadgeClass(pr.status)}>{statusLabel(pr.status)}</Badge>
                    </td>
                    <td className="px-4 py-2 text-center text-xs">{pr.created_at?.split('T')[0]}</td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => onView(pr)} title="View" data-testid={`view-pr-${pr.id}`}><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => onDownloadPdf(pr.id, pr.prn_number)} title="Download PDF" data-testid={`download-pr-${pr.id}`}><Download className="w-4 h-4 text-indigo-600" /></Button>
                        {isDraft && onEdit && (
                          <Button variant="ghost" size="sm" onClick={() => onEdit(pr)} title="Edit Draft" data-testid={`edit-pr-${pr.id}`} className="text-amber-600"><Pencil className="w-4 h-4" /></Button>
                        )}
                        {isDraft && onFinalize && (
                          <Button variant="ghost" size="sm" onClick={() => onFinalize(pr.id)} title="Finalize (deduct stock)" data-testid={`finalize-pr-${pr.id}`} className="text-green-600"><CheckCircle2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
